import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { InventoryMutation } from '@domain/inventory/models/InventoryMutation';
import { useState } from 'react';
import { ProductFilter } from '@domain/product/models/ProductFilter';

export const useWarehouse = (id?: string, filter?:ProductFilter) => {
    const queryClient = useQueryClient();
    
    const [localPage, setPage] = useState(1);
    const [localLimit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const page = filter?.page ?? localPage;
    const limit = filter?.limit ?? localLimit;

    const {
        data: warehouse,
        isLoading: isLoadingWarehouse,
        error: warehouseError
    } = useQuery({
        queryKey: ['warehouse', id],
        queryFn: async () => {
            if (!id) return null;
            const result = await container.warehouseRepository.getWarehouse(id);

            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!id
    });

    const {
        data: inventory,
        isLoading: isLoadingInventory,
        error: inventoryError
    } = useQuery({
        queryKey: ['warehouse-inventory', id, filter, page, limit],
        queryFn: async () => {
            if (!id) return null;
            const result = await container.inventoryRepository.getInventoryForWarehouse(id, filter, page, limit);

            if (result.status === 'error') throw new Error(result.error.message);

            setTotalPages(result.totalPages);

            return result.data;
        },
        enabled: !!id
    });

    const { mutateAsync: submitMutation } = useMutation<void, Error, { mutations: Omit<InventoryMutation, 'id' | 'createdAt'>[], date: Date }>({
        mutationFn: async ({ mutations, date }) => {
            const result = await container.inventoryRepository.submitManualMutation(mutations, date);

            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['warehouse', id]);
            queryClient.invalidateQueries(['warehouse-inventory', id]);
        }
    });

    return {
        warehouse,
        inventory,
        page,
        setPage,
        limit,
        setLimit,
        totalPages,
        isLoading: isLoadingWarehouse || isLoadingInventory,
        error: warehouseError || inventoryError,
        submitMutation
    };
}; 